import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faLock, 
  faSignInAlt, 
  faUserPlus, 
  faEye, 
  faEyeSlash, 
  faSpinner,
  faCheckCircle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import JSEncrypt from 'jsencrypt';

function Login() {
  const [formData, setFormData] = useState({
    clinicName: '',
    password: '',
    showPassword: false,
  });

  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // ── Detect mobile device ──
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = typeof window.navigator === 'undefined' ? '' : navigator.userAgent;
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ── Set theme color for browser on mobile (white status bar) ──
  useEffect(() => {
    if (isMobile) {
      // Set the theme-color meta tag for mobile browsers
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', '#ffffff');
      
      // Also set the status bar style for iOS
      let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!metaStatusBar) {
        metaStatusBar = document.createElement('meta');
        metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
        document.head.appendChild(metaStatusBar);
      }
      metaStatusBar.setAttribute('content', 'black-translucent');
    }
  }, [isMobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const togglePasswordVisibility = () => {
    setFormData({ ...formData, showPassword: !formData.showPassword });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.clinicName === '' || formData.password === '') {
      toast.error('Please fill in all fields.', { position: 'top-center' });
      return;
    }

    setLoading(true);
    try {
      const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAup3FU135mAvJT6OheYW3
pQyWf6jvS4duUMY4cXrlJXyGqu8HqvTU0ewPy6w2HhCPxWboNclkAkPhOCc4URNT
x1Grg+mCsWmfhVimP2wtfmlBCJ09cyDMYf93iGj8RFf3CshY5yhppT/pX+RgTuXw
ClpOXe24CLG2VF9suNylk+ReAMLyOxaekYofAMBvvrD4+GYPJgvkTMXCXCKp2PnO
8+OjiltNMnoyqPEZoXHTV4EXtTrjYnwzSe0WZSSuzgVMhmtdx+IS4eisSumHV1eI
wBeZwI0bYGxDCedPRassmSFgTFqkkcgIXmEP1n5w/08S/QPr2G+myKTeRqp5RJA5
PQIDAQAB
-----END PUBLIC KEY-----`;

      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedPassword = encrypt.encrypt(formData.password);

      const encryptedFormData = { ...formData, password: encryptedPassword, confirmPassword: encryptedPassword };

      const response = await fetch(urls.loginClinic, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encryptedFormData),
      });

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        const sessionToken = responseData.sessionToken;
        navigate(`/dashboard?token=${sessionToken}`);
        toast.success('Login successful!', { position: 'top-center' });
      } else {
        toast.error('Login failed. Please check your credentials.', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error logging in:', error);
      toast.error('Login failed!', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      backgroundColor: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      padding: '20px',
      boxSizing: 'border-box',
    }}>
      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Main Card Container */}
      <div style={{
        maxWidth: '480px',
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '24px',
        boxShadow: '0 20px 35px -10px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.01)',
        padding: '32px 28px',
        transition: 'all 0.3s ease',
        border: '1px solid #f0f0f0',
      }}>
        
        {/* Header Section */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'linear-gradient(135deg, #007CF0 0%, #0068FF 100%)',
            borderRadius: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 20px -5px rgba(0, 124, 240, 0.2)',
          }}>
            <FontAwesomeIcon icon={faBuilding} size="xl" style={{ color: 'white' }} />
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            margin: '0 0 8px',
            letterSpacing: '-0.3px',
            color: '#1a1a1a',
          }}>Welcome Back</h1>
          <p style={{
            fontSize: '15px',
            fontWeight: '400',
            margin: '0',
            color: '#666666',
          }}>Sign in to your Business Account</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          {/* Business Name Field */}
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '24px' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#333333',
              marginBottom: '8px',
            }}>
              <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '8px', color: '#007CF0', fontSize: '12px' }} /> 
              Business Name
            </label>
            <input
              type="text"
              name="clinicName"
              value={formData.clinicName}
              onChange={handleChange}
              required
              placeholder="Enter your business name"
              style={{
                padding: '12px 14px',
                fontSize: '14px',
                border: '1.5px solid #e8e8e8',
                borderRadius: '12px',
                outline: 'none',
                transition: 'all 0.2s ease',
                fontFamily: 'inherit',
                backgroundColor: '#ffffff',
                color: '#1a1a1a',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#007CF0'}
              onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
            />
          </div>

          {/* Password Field */}
          <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '28px' }}>
            <label style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#333333',
              marginBottom: '8px',
            }}>
              <FontAwesomeIcon icon={faLock} style={{ marginRight: '8px', color: '#007CF0', fontSize: '12px' }} /> 
              Password
            </label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={formData.showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
                style={{
                  padding: '12px 48px 12px 14px',
                  fontSize: '14px',
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff',
                  color: '#1a1a1a',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#007CF0'}
                onBlur={(e) => e.target.style.borderColor = '#e8e8e8'}
              />
              <FontAwesomeIcon
                icon={formData.showPassword ? faEyeSlash : faEye}
                onClick={togglePasswordVisibility}
                style={{
                  position: 'absolute',
                  right: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#999999',
                  fontSize: '16px',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#007CF0'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#999999'}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: '1',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: '600',
                color: 'white',
                background: 'linear-gradient(135deg, #007CF0 0%, #0068FF 100%)',
                border: 'none',
                borderRadius: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSignInAlt} />
                  <span>Log In</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/clinic-registration')}
              style={{
                flex: '1',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#059669',
                background: '#ffffff',
                border: '1.5px solid #059669',
                borderRadius: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#059669';
              }}
            >
              <FontAwesomeIcon icon={faUserPlus} />
              <span>Create Account</span>
            </button>
          </div>
        </form>

        {/* Info Cards */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginTop: '32px',
          padding: '16px 0 0 0',
          borderTop: '1px solid #f0f0f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#555555' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#007CF0', fontSize: '13px' }} />
            <span>Secure & Encrypted Login</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#555555' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#007CF0', fontSize: '13px' }} />
            <span>24/7 System Availability</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#555555' }}>
            <FontAwesomeIcon icon={faArrowRight} style={{ color: '#007CF0', fontSize: '12px' }} />
            <span>Multi-business support platform</span>
          </div>
        </div>

        {/* Footer Text */}
        <div style={{
          marginTop: '24px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#999999',
        }}>
          © 2025 Business Suite. All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default Login;